import ConfirmDialog from "@/components/common/ConfirmDialog";
import DepartmentSelect from "@/components/DepartmentSelect";
import RejectionDialog from "@/components/dialogs/RejectionDialog";
import { useAppSettings } from "@/services/commonService";
import { useInstructionStatusChange } from "@/services/instructionService";
import { CodeValue, DEFAULT_FILE_SETTINGS, SUBMIT_TYPE } from "@/types/common";
import { DepartmentRoleType, DepartmentTreeNode } from "@/types/department";
import {
  AttachmentFile,
  INSTRUCTION_STATUS,
  INSTRUCTION_TYPE,
  InstructionFormData,
  InstructionFormProps,
  InstructionPermissions,
} from "@/types/instruction";
import { downloadFile } from "@/utils/downloadUtils";
import { formatFileSize } from "@/utils/formatUtils";
import { updateObjectState } from "@/utils/updateState";
import { Delete as DeleteIcon, Save as SaveIcon } from "@mui/icons-material";
import { Box, Button, Chip, Divider, FormControl, InputAdornment, MenuItem, Paper, Select, Stack, TextField, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import React, { useEffect, useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";

dayjs.extend(isBetween);

const DirectiveForm: React.FC<InstructionFormProps> = ({
  id,
  user,
  detailData,
  codeValues,
  initialData,
  onSubmit,
  onDelete,
  onCancel,
  onReject,
  onExecuteRequest,
  onCompleteRequest,
  onComplete,
}) => {
  const { data: appSettings } = useAppSettings();
  const fileSettings = useMemo(() => appSettings?.file ?? DEFAULT_FILE_SETTINGS, [appSettings]);
  const statusChangeMutation = useInstructionStatusChange();

  const [rejectionDialogOpen, setRejectionDialogOpen] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    content: string;
    onConfirm: () => void;
  }>({ open: false, title: "", content: "", onConfirm: () => {} });
  // 최초 마운트 시에만 초기화
  const [formData, setFormData] = useState<InstructionFormData>(() => ({
    ...initialData,
    uiType: initialData?.uiType ?? undefined,
    uiStatus: initialData?.uiStatus ?? undefined,
    attachments: initialData?.attachments ?? [],
  }));

  const [errors, setErrors] = useState<Record<string, boolean>>({
    occurredDate: false,
    dueDate: false,
    title: false,
    summary: false,
    ownerDepartment: false,
    ccDepartments: false,
  });

  const [uploadAttachments, setUploadAttachments] = useState<{ file: File; tempId: string }[]>([]);

  const isPolicy = formData.uiType === INSTRUCTION_TYPE.POLICY;
  const isInstruction = formData.uiType === INSTRUCTION_TYPE.INSTRUCTION;

  const isCreate = !id;
  const isApprover = user?.role === "APPROVER";
  const status = formData.uiStatus;

  // 상태 비교 로직 수정
  const isWriting = formData.uiStatus === INSTRUCTION_STATUS.WRITING;
  const isPendingReview = formData.uiStatus === INSTRUCTION_STATUS.PENDING_REVIEW;
  const isInProgress = formData.uiStatus === INSTRUCTION_STATUS.IN_PROGRESS;
  const isCompletionRequested = formData.uiStatus === INSTRUCTION_STATUS.COMPLETION_REQUESTED;
  const isClosed = formData.uiStatus === INSTRUCTION_STATUS.CLOSED;
  const isDelayed = formData.uiStatus === INSTRUCTION_STATUS.DELAYED;
  const isOwner = detailData?.createdBy === user?.id;

  const isEditable = isCreate || (isOwner && status === INSTRUCTION_STATUS.WRITING);
  const isEditReport =
    Boolean(isEditable) ||
    ((isInProgress || isDelayed) &&
      isInstruction &&
      user?.affiliatedDepartments
        .filter((d: any) => (d.isHead || isApprover) && d.departmentId === formData.ownerDepartment?.id)
        .map((d: any) => d.departmentId)
        .includes(formData.ownerDepartment?.id));
  const canEditAttachments = Boolean(isEditReport);
  const showReportField = isInstruction && (isInProgress || isCompletionRequested || isClosed || isDelayed);
  const titleLabel = isInstruction ? "지시 상황" : "방침 상황";
  const summaryLabel = isInstruction ? "지시 요약" : "방침 요약";
  const detailLabel = isInstruction ? "상세 지시내용" : "상세 방침내용";

  // 서버에서 받은 권한 정보 사용 (생성 모드에서는 기본값 사용)
  const permissions: InstructionPermissions = detailData?.permissions ?? {
    canEdit: isCreate,
    canDelete: false,
    canSubmit: isCreate,
    canChangeStatus: false,
    canConfirm: false,
    canReject: false,
    isApprover: isApprover,
  };

  // 상태 목록 가져오기
  const statusList: CodeValue[] = codeValues?.INSTRUCTION_STATUS ?? [];
  const showRejectionDialog = permissions.canReject;

  const updateAttachmentsProtection = () => {
    setFormData((prev) => ({
      ...prev,
      attachments: (prev.attachments ?? []).map((file) => ({
        ...file,
        isProtected: !file.id ? false : file.isProtected,
      })),
    }));
  };

  useEffect(() => {
    updateAttachmentsProtection();
  }, [isInProgress, (formData.attachments ?? []).length]);

  const handleInputChange = (field: keyof InstructionFormData) => (event: React.ChangeEvent<HTMLInputElement>) => {
    updateObjectState(setFormData, field, event.target.value);
  };

  const handleDateChange = (field: "occurredDate" | "dueDate" | "reportDate") => (newValue: dayjs.Dayjs | null) => {
    if (newValue && dayjs(newValue).isValid()) {
      updateObjectState(setErrors, field, false);
      updateObjectState(setFormData, field, newValue.format("YYYY-MM-DD"));
    } else {
      updateObjectState(setFormData, field, null);
    }
  };

  const onDrop = (acceptedFiles: File[], rejectedFiles: any[]) => {
    if (rejectedFiles.length > 0) {
      alert("첨부 최대 개수를 초과 했거나 허용되지 않는 파일, 크기입니다.");
      return;
    }

    const currentCount = formData.attachments?.filter((att) => !att.isProtected).length ?? 0;
    if (currentCount + acceptedFiles.length > fileSettings.maxCount) {
      alert(`최대 ${fileSettings.maxCount}개의 파일만 첨부할 수 있습니다.`);
      return;
    }

    const tempIdBase = Date.now();
    const attachmentFiles: AttachmentFile[] = acceptedFiles.map((file, idx) => ({
      originalFilename: file.name,
      fileSize: file.size,
      isProtected: false,
      tempId: `${tempIdBase}_${idx} `,
    }));

    updateObjectState(setFormData, "attachments", [...(formData.attachments ?? []), ...attachmentFiles]);
    setUploadAttachments((prev) => [
      ...prev,
      ...acceptedFiles.map((file, idx) => ({ file, tempId: `${tempIdBase}_${idx}` })),
    ]);
  };

  const removeFile = (target: AttachmentFile) => {
    updateObjectState(
      setFormData,
      "attachments",
      (formData.attachments ?? []).filter((att) => (att.id ? att.id !== target.id : att.tempId !== target.tempId))
    );

    if (target.tempId) {
      setUploadAttachments((prev) => prev.filter((f) => f.tempId !== target.tempId));
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: fileSettings.accept,
    maxSize: fileSettings.maxSize,
    maxFiles: fileSettings.maxCount,
    disabled: !canEditAttachments,
  });

  const handleDownloadFile = (file: AttachmentFile) => {
    if (file.id) {
      downloadFile(`/common/instructions/${formData.id}/attachments/${file.id}/download`, file.originalFilename);
    }
  };

  const handleOpenRejectionDialog = () => {
    setRejectionDialogOpen(true);
  };

  const handleCloseRejectionDialog = () => {
    setRejectionDialogOpen(false);
  };

  const handleSaveRejection = (reason: string) => {
    if (onReject) {
      onReject(reason);
    }

    handleCloseRejectionDialog();
  };

  // 삭제 확인 다이얼로그
  const handleDeleteConfirm = () => {
    setConfirmDialog({
      open: true,
      title: "삭제 확인",
      content: `[${formData.title}] 지시사항을 삭제하시겠습니까?\n\n⚠️ 삭제된 데이터는 복구할 수 없습니다.`,
      onConfirm: () => {
        onDelete?.();
        setConfirmDialog({ ...confirmDialog, open: false });
      },
    });
  };

  // 상태 변경 핸들러
  const handleStatusChange = (targetStatusId: number) => {
    const targetStatus = statusList.find((s) => s.id === targetStatusId);
    if (!targetStatus || !formData.id || targetStatusId === formData.status) return;

    setConfirmDialog({
      open: true,
      title: "상태 변경 확인",
      content: `지시사항 상태를 [${targetStatus.label}](으)로 변경하시겠습니까?\n\n⚠️ 상태 변경 이력이 기록됩니다.`,
      onConfirm: async () => {
        await statusChangeMutation.mutateAsync({
          id: formData.id!,
          request: { targetStatus: targetStatusId },
        });
        // 로컬 상태 업데이트
        setFormData((prev) => ({
          ...prev,
          status: targetStatusId,
          uiStatus: targetStatus.code,
        }));
        setConfirmDialog({ ...confirmDialog, open: false });
      },
    });
  };

  const handleCloseConfirmDialog = () => {
    setConfirmDialog({ ...confirmDialog, open: false });
  };

  // DepartmentSelect onChange handlers
  const handleOwnerDepartmentChange = (departments: DepartmentTreeNode[]) => {
    const department = departments[0] ? { id: departments[0].id, label: departments[0].label } : undefined;
    if (department?.id !== -1) {
      updateObjectState(setErrors, "ownerDepartment", false);
    }
    updateObjectState(setFormData, "ownerDepartment", department);
  };

  const handleCcDepartmentsChange = (departments: DepartmentTreeNode[]) => {
    const limitedDepartments = departments.slice(0, 7).map((d) => ({
      id: d.id,
      label: d.label,
    }));

    if (limitedDepartments.length > 0) {
      updateObjectState(setErrors, "ccDepartments", false);
    }

    updateObjectState(setFormData, "ccDepartments", limitedDepartments);
  };

  const validateForm = () => {
    const newErrors = Object.fromEntries(Object.keys(errors).map((key) => [key, false]));

    if (!formData.occurredDate) {
      newErrors.occurredDate = true;
      setErrors(newErrors);
      return alert("발생일자를 입력해주세요.");
    }

    if (isInstruction && !formData.dueDate) {
      newErrors.dueDate = true;
      setErrors(newErrors);
      return alert("기한일을 입력해주세요.");
    }

    if (!formData.ownerDepartment || -1 === formData.ownerDepartment.id) {
      newErrors.ownerDepartment = true;
      setErrors(newErrors);
      return alert("수명부서를 선택해주세요.");
    }

    if (!formData.title) {
      newErrors.title = true;
      setErrors(newErrors);
      return alert("상황을 입력해주세요.");
    }

    if (!formData.summary) {
      newErrors.summary = true;
      setErrors(newErrors);
      return alert("요약을 입력해주세요.");
    }

    if (isInstruction && !formData.dueDate) {
      newErrors.dueDate = true;
      setErrors(newErrors);
      return alert("기한일을 입력해주세요.");
    }

    setErrors(newErrors);
    return Object.values(newErrors).filter(Boolean).length === 0;
  };

  const handleSubmit = (submitType?: string) => {
    if (validateForm()) {
      onSubmit?.(
        formData,
        uploadAttachments.map((f) => f.file),
        submitType
      );
    }
  };

  const handleCompleteRequest = () => {
    const newErrors = { report: false, reportDate: false };
    if (!isInstruction) {
      return;
    }

    if (!formData.reportDate) {
      newErrors.reportDate = true;
      setErrors(newErrors);
      return alert("보고일자를 입력해주세요.");
    }

    if (!formData.report) {
      newErrors.report = true;
      setErrors(newErrors);
      return alert("보고사항을 입력해주세요.");
    }

    if (Object.values(newErrors).filter(Boolean).length === 0 && onCompleteRequest) {
      onCompleteRequest(
        formData,
        uploadAttachments.map((f) => f.file)
      );
    }
  };

  return (
    <Box
      sx={{
        height: "auto",
        minHeight: "unset",
        overflow: "visible",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          bgcolor: "#fff",
        }}
      >
        <Stack spacing={0}>
          <Box sx={{ display: "flex", alignItems: "stretch" }}>
            <Typography sx={{ width: 150, bgcolor: "#f5f5f5", p: 2, display: "flex", alignItems: "center" }}>
              발생일자
              <Box component='span' sx={{ color: "error.main", ml: 0.5 }}>
                *
              </Box>
            </Typography>
            <Box sx={{ flex: 1, display: "flex", alignItems: "stretch" }}>
              <Box sx={{ width: 300, p: 2, display: "flex", alignItems: "center" }}>
                {isEditable ? (
                  <DatePicker
                    value={formData.occurredDate ? dayjs(formData.occurredDate as string) : null}
                    maxDate={formData.dueDate ? dayjs(formData.dueDate as string) : undefined}
                    onChange={handleDateChange("occurredDate")}
                    format='YYYY-MM-DD'
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: "medium",
                        required: true,
                        error: errors.occurredDate,
                        sx: {
                          "& .MuiInputBase-root": {
                            height: "40px",
                          },
                        },
                      },
                    }}
                  />
                ) : (
                  <Typography>
                    {formData.occurredDate ? dayjs(formData.occurredDate).format("YYYY-MM-DD") : "-"}
                  </Typography>
                )}
              </Box>
              {isInstruction && (
                <>
                  <Typography
                    sx={{
                      width: 100,
                      bgcolor: "#f5f5f5",
                      p: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderLeft: "1px solid #e0e0e0",
                    }}
                  >
                    기한일
                    <Box component='span' sx={{ color: "error.main", ml: 0.5 }}>
                      *
                    </Box>
                  </Typography>
                  <Box sx={{ width: 300, p: 2, display: "flex", alignItems: "center" }}>
                    {isEditable ? (
                      <DatePicker
                        value={formData.dueDate ? dayjs(formData.dueDate) : null}
                        minDate={dayjs(formData.occurredDate).isAfter(dayjs()) ? dayjs(formData.occurredDate) : dayjs()}
                        onChange={handleDateChange("dueDate")}
                        format='YYYY-MM-DD'
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            size: "medium",
                            required: true,
                            error: errors.dueDate,
                            sx: {
                              "& .MuiInputBase-root": {
                                height: "40px",
                              },
                            },
                          },
                        }}
                      />
                    ) : (
                      <Typography>
                        {formData.dueDate ? dayjs(formData.dueDate).format("YYYY-MM-DD") : "-"}
                      </Typography>
                    )}
                  </Box>
                </>
              )}
              {showReportField && (
                <>
                  <Typography
                    sx={{
                      width: 100,
                      bgcolor: "#f5f5f5",
                      p: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderLeft: "1px solid #e0e0e0",
                    }}
                  >
                    보고일
                    <Box component='span' sx={{ color: "error.main", ml: 0.5 }}>
                      *
                    </Box>
                  </Typography>
                  <Box sx={{ flex: 1, p: 2, display: "flex", alignItems: "center" }}>
                    {isEditReport ? (
                      <DatePicker
                        value={formData.reportDate ? dayjs(formData.reportDate) : null}
                        minDate={
                          dayjs(formData.occurredDate).isBefore(dayjs()) ? dayjs(formData.occurredDate) : dayjs()
                        }
                        onChange={handleDateChange("reportDate")}
                        format='YYYY-MM-DD'
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            size: "medium",
                            required: true,
                            error: errors.reportDate,
                            sx: {
                              "& .MuiInputBase-root": {
                                height: "40px",
                              },
                            },
                          },
                        }}
                      />
                    ) : (
                      <Typography>
                        {formData.reportDate ? dayjs(formData.reportDate).format("YYYY-MM-DD") : "-"}
                      </Typography>
                    )}
                  </Box>
                </>
              )}
            </Box>
          </Box>

          <Divider />

          <Box sx={{ display: "flex", alignItems: "stretch" }}>
            <Typography sx={{ width: 150, bgcolor: "#f5f5f5", p: 2, display: "flex", alignItems: "center" }}>
              수명부서
              <Box component='span' sx={{ color: "error.main", ml: 0.5 }}>
                *
              </Box>
            </Typography>
            <Box sx={{ flex: 1, display: "flex", alignItems: "stretch" }}>
              <Box sx={{ width: 300, p: 2, display: "flex", alignItems: "center" }}>
                {isEditable ? (
                  <DepartmentSelect
                    items={formData.ownerDepartment ? [formData.ownerDepartment] : []}
                    onChange={handleOwnerDepartmentChange}
                    maxSelected={1}
                    placeholder='수명부서 선택'
                    required
                    error={errors.ownerDepartment}
                    roleType={DepartmentRoleType.OWNER}
                    isCompanyWide={isPolicy}
                  />
                ) : (
                  <Typography>{formData.ownerDepartment?.fullLabel ?? "-"}</Typography>
                )}
              </Box>
              <Typography
                sx={{
                  width: 100,
                  bgcolor: "#f5f5f5",
                  p: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderLeft: "1px solid #e0e0e0",
                }}
              >
                협조부서
              </Typography>
              <Box sx={{ flex: 1, p: 2, display: "flex", alignItems: "center" }}>
                {isEditable ? (
                  <DepartmentSelect
                    items={formData.ccDepartments}
                    onChange={handleCcDepartmentsChange}
                    maxSelected={7}
                    placeholder='협조부서 선택 (최대 7개)'
                    roleType={DepartmentRoleType.CC}
                    sx={{
                      width: "100%",
                    }}
                  />
                ) : (
                  <Typography>
                    {formData.ccDepartments?.map((d) => d.fullLabel).join(", ") ?? "-"}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>

          <Divider />

          <Box sx={{ display: "flex", alignItems: "stretch" }}>
            <Typography sx={{ width: 150, bgcolor: "#f5f5f5", p: 2, display: "flex", alignItems: "center" }}>
              {titleLabel}
              <Box component='span' sx={{ color: "error.main", ml: 0.5 }}>
                *
              </Box>
            </Typography>
            <Box sx={{ flex: 1, p: 2, display: "flex", alignItems: "center" }}>
              {isEditable ? (
                <TextField
                  fullWidth
                  required
                  size='medium'
                  value={formData.title ?? ""}
                  onChange={handleInputChange("title")}
                  error={errors.title}
                  inputProps={{ maxLength: 100 }}
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position='end'>
                          <span style={{ whiteSpace: "nowrap", fontSize: "0.8rem" }}>
                            {`${formData.title?.length ?? 0}/100 자`}
                          </span>
                        </InputAdornment>
                      ),
                    },
                  }}
                  sx={{
                    "& .MuiInputBase-root": {
                      height: "40px",
                    },
                  }}
                />
              ) : (
                <Typography>{formData.title ?? "-"}</Typography>
              )}
            </Box>
            {permissions.canChangeStatus && !isCreate && (
              <>
                <Typography sx={{ width: 100, bgcolor: "#f5f5f5", p: 2, display: "flex", alignItems: "center" }}>
                  상태변경
                </Typography>
                <Box sx={{ p: 2, display: "flex", alignItems: "center" }}>
                  <FormControl size='small' sx={{ minWidth: 120 }}>
                    <Select
                      value={formData.status ?? ""}
                      displayEmpty
                      onChange={(e) => handleStatusChange(Number(e.target.value))}
                      sx={{ height: "40px" }}
                    >
                      {statusList.map((s) => (
                        <MenuItem key={s.id} value={s.id}>
                          {s.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </>
            )}
          </Box>

          <Divider />

          <Box sx={{ display: "flex", alignItems: "stretch" }}>
            <Typography sx={{ width: 150, bgcolor: "#f5f5f5", p: 2, display: "flex", alignItems: "center" }}>
              {summaryLabel}
              <Box component='span' sx={{ color: "error.main", ml: 0.5 }}>
                *
              </Box>
            </Typography>
            <Box sx={{ flex: 1, p: 2, display: "flex", alignItems: "center" }}>
              {isEditable ? (
                <TextField
                  fullWidth
                  required
                  size='medium'
                  value={formData.summary ?? ""}
                  onChange={handleInputChange("summary")}
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position='end'>
                          <span style={{ whiteSpace: "nowrap", fontSize: "0.8rem" }}>
                            {`${formData.summary?.length ?? 0}/255 자`}
                          </span>
                        </InputAdornment>
                      ),
                    },
                  }}
                  inputProps={{ maxLength: 255 }}
                  error={errors.summary}
                  sx={{
                    "& .MuiInputBase-root": {
                      height: "40px",
                    },
                  }}
                />
              ) : (
                <Typography>{formData.summary ?? "-"}</Typography>
              )}
            </Box>
          </Box>

          <Divider />

          <Box sx={{ display: "flex", alignItems: "stretch", minHeight: 250 }}>
            <Typography sx={{ width: 150, bgcolor: "#f5f5f5", p: 2, display: "flex", alignItems: "flex-start" }}>
              {detailLabel}
            </Typography>
            <Box sx={{ flex: 1, p: 2 }}>
              {isEditable ? (
                <TextField
                  fullWidth
                  required
                  multiline
                  rows={8}
                  size='medium'
                  value={formData.detail}
                  onChange={handleInputChange("detail")}
                />
              ) : (
                <TextField
                  fullWidth
                  required
                  multiline
                  rows={8}
                  size='medium'
                  value={formData.detail}
                  disabled
                  sx={{
                    bgcolor: "grey.50",
                    "& .MuiInputBase-root": {
                      minHeight: "100px",
                    },
                  }}
                />
              )}
            </Box>
          </Box>

          {showReportField && (
            <>
              <Divider />
              <Box sx={{ display: "flex", alignItems: "stretch", minHeight: 250 }}>
                <Typography sx={{ width: 150, bgcolor: "#f5f5f5", p: 2, display: "flex", alignItems: "flex-start" }}>
                  주요 보고사항
                  <Box component='span' sx={{ color: "error.main", ml: 0.5 }}>
                    *
                  </Box>
                </Typography>
                <Box sx={{ flex: 1, p: 2 }}>
                  {isEditReport ? (
                    <TextField
                      fullWidth
                      multiline
                      rows={8}
                      size='medium'
                      value={formData.report}
                      error={errors.report}
                      onChange={handleInputChange("report")}
                    />
                  ) : (
                    <TextField
                      fullWidth
                      multiline
                      rows={8}
                      size='medium'
                      value={formData.report}
                      disabled
                      sx={{
                        bgcolor: "grey.50",
                        "& .MuiInputBase-root": {
                          minHeight: "100px",
                        },
                      }}
                    />
                  )}
                </Box>
              </Box>
            </>
          )}

          <Divider />

          <Box sx={{ display: "flex", alignItems: "stretch" }}>
            <Typography sx={{ width: 150, bgcolor: "#f5f5f5", p: 2, display: "flex", alignItems: "flex-start" }}>
              첨부파일
            </Typography>
            <Box sx={{ flex: 1, p: 2 }}>
              {canEditAttachments && (
                <Box
                  {...getRootProps()}
                  sx={{
                    border: "2px dashed",
                    borderColor: isDragActive ? "primary.main" : "grey.300",
                    borderRadius: 1,
                    p: 3,
                    textAlign: "center",
                    bgcolor: "grey.50",
                    cursor: "pointer",
                    "&:hover": {
                      borderColor: "primary.main",
                    },
                  }}
                >
                  <input {...getInputProps()} />
                  {isDragActive ? (
                    <Typography>파일을 여기에 놓으세요...</Typography>
                  ) : (
                    <Typography>
                      파일을 드래그 앤 드롭하거나 클릭하여 선택하세요 (최대 {fileSettings.maxCount}개 파일, 각 파일당{" "}
                      {formatFileSize(fileSettings.maxSize)} 이하)
                    </Typography>
                  )}
                </Box>
              )}

              {(formData.attachments ?? []).length > 0 && (
                <Box sx={{ mt: canEditAttachments ? 2 : 0, display: "flex", gap: 1, flexWrap: "wrap" }}>
                  {(formData.attachments ?? []).map((file) => (
                    <Chip
                      key={file.id ?? file.tempId}
                      label={`${file.originalFilename} (${formatFileSize(file.fileSize)})`}
                      onDelete={canEditAttachments && !file.isProtected ? () => removeFile(file) : undefined}
                      sx={{ bgcolor: "grey.100", fontSize: 14, height: 36 }}
                      clickable={!!file.id}
                      onClick={file.id ? () => handleDownloadFile(file) : undefined}
                    />
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        </Stack>
      </Paper>

      <Box sx={{ mt: 3, display: "flex", justifyContent: "center", gap: 1, flexWrap: "wrap" }}>
        {onCancel && (
          <Button variant='outlined' onClick={onCancel} sx={{ minWidth: "100px", height: "36px" }}>
            목록으로
          </Button>
        )}
        {permissions.canDelete && (
          <Button
            variant='outlined'
            color='error'
            startIcon={<DeleteIcon />}
            onClick={handleDeleteConfirm}
            sx={{ minWidth: "100px", height: "36px" }}
          >
            삭제
          </Button>
        )}
        {isWriting && permissions.canEdit && (
          <Button
            variant='contained'
            startIcon={<SaveIcon />}
            onClick={() => handleSubmit(SUBMIT_TYPE.SAVE)}
            sx={{ minWidth: "100px", height: "36px" }}
          >
            저장
          </Button>
        )}
        {isWriting && permissions.canSubmit && (
          <Button
            variant='contained'
            color='primary'
            onClick={() => handleSubmit(SUBMIT_TYPE.REGISTRATION_REQUESTED)}
            sx={{ minWidth: "100px", height: "36px" }}
          >
            등록요청
          </Button>
        )}
        {permissions.canReject && (
          <Button
            variant='contained'
            color='warning'
            onClick={handleOpenRejectionDialog}
            sx={{ minWidth: "100px", height: "36px" }}
          >
            반려
          </Button>
        )}
        {isInstruction && isPendingReview && permissions.canConfirm && (
          <Button
            variant='contained'
            color='primary'
            onClick={() => onExecuteRequest?.()}
            sx={{ minWidth: "100px", height: "36px" }}
          >
            이행확정
          </Button>
        )}
        {(isInProgress || isDelayed) && isInstruction && isEditReport && (
          <Button
            variant='contained'
            color='primary'
            onClick={handleCompleteRequest}
            sx={{ minWidth: "100px", height: "36px" }}
          >
            완료요청
          </Button>
        )}
        {((isInstruction && isCompletionRequested) || (isPolicy && isPendingReview)) && permissions.canConfirm && (
          <Button variant='contained' color='primary' onClick={onComplete} sx={{ minWidth: "100px", height: "36px" }}>
            완료확정
          </Button>
        )}
      </Box>

      {showRejectionDialog && (
        <RejectionDialog open={rejectionDialogOpen} onClose={handleCloseRejectionDialog} onSave={handleSaveRejection} />
      )}

      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        content={confirmDialog.content}
        onCancel={handleCloseConfirmDialog}
        onConfirm={confirmDialog.onConfirm}
      />
    </Box>
  );
};

export default DirectiveForm;
